import JSZip from 'jszip';

// Types for image data
interface ImageData {
  id: string;
  name: string;
  imagekey: string;
  url: string;
  path: string;
}

interface LocaKey {
  key: string;
  sourceText?: string;
}

interface ExportData {
  uxml: string;
  uss: string;
  images: Map<string, Blob>;
  locaKeys: LocaKey[];
  imageKeys: string[];
}

/**
 * Converts HTML to Unity UXML format
 */
export function htmlToUxml(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<root>${html}</root>`, 'text/html');
  
  const convertNode = (node: Node, indent: string = '  '): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim() || '';
      if (!text) return '';
      
      // Check if text contains LocaKey pattern
      if (text.match(/\{\{?[a-zA-Z0-9_.-]+\}\}?/)) {
        // Return text with LocaKey preserved
        return text;
      }
      
      return text;
    }
    
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    
    const element = node as Element;
    const tagName = element.tagName.toLowerCase();
    
    // Skip script and style tags
    if (tagName === 'script' || tagName === 'style') return '';
    
    let uxmlTag = '';
    let attributes = '';
    
    // Get classes
    const classes = element.className;
    if (classes) {
      attributes += ` class="${classes}"`;
    }
    
    // Get other attributes
    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      if (attr.name !== 'class' && attr.name !== 'style') {
        attributes += ` ${attr.name}="${attr.value}"`;
      }
    }
    
    // Map HTML elements to Unity UI elements
    switch (tagName) {
      case 'button': {
        uxmlTag = 'ui:Button';
        // Extract text content for button
        const buttonText = element.textContent?.trim() || '';
        if (buttonText) {
          attributes += ` text="${buttonText}"`;
        }
        break;
      }
      
      case 'input': {
        const inputType = element.getAttribute('type') || 'text';
        if (inputType === 'text' || inputType === 'password') {
          uxmlTag = 'ui:TextField';
        } else if (inputType === 'checkbox') {
          uxmlTag = 'ui:Toggle';
        } else {
          uxmlTag = 'ui:VisualElement';
        }
        break;
      }
      
      case 'label':
      case 'span':
      case 'p':
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6': {
        uxmlTag = 'ui:Label';
        const labelText = element.textContent?.trim() || '';
        if (labelText) {
          attributes += ` text="${labelText}"`;
        }
        break;
      }
      
      case 'img': {
        uxmlTag = 'ui:VisualElement';
        // Image source will be handled via USS background-image
        const imgSrc = element.getAttribute('src');
        if (imgSrc) {
          attributes += ` data-image-src="${imgSrc}"`;
        }
        break;
      }
      
      case 'select':
        uxmlTag = 'ui:DropdownField';
        break;
      
      case 'textarea':
        uxmlTag = 'ui:TextField';
        attributes += ' multiline="true"';
        break;
      
      case 'div':
      case 'section':
      case 'article':
      case 'header':
      case 'footer':
      case 'main':
      case 'nav':
      default:
        uxmlTag = 'ui:VisualElement';
        break;
    }
    
    // Check if element has children
    const hasElementChildren = Array.from(element.childNodes).some(
      child => child.nodeType === Node.ELEMENT_NODE
    );
    
    // For elements with text content but no element children (like buttons/labels)
    if (!hasElementChildren && (tagName === 'button' || tagName === 'label' || 
        tagName === 'span' || tagName === 'p' || tagName.startsWith('h'))) {
      return `${indent}<${uxmlTag}${attributes} />\n`;
    }
    
    // For elements with children
    let result = `${indent}<${uxmlTag}${attributes}`;
    
    const childrenXml: string[] = [];
    element.childNodes.forEach(child => {
      const childXml = convertNode(child, indent + '  ');
      if (childXml) childrenXml.push(childXml);
    });
    
    if (childrenXml.length === 0) {
      result += ' />\n';
    } else {
      result += '>\n';
      result += childrenXml.join('');
      result += `${indent}</${uxmlTag}>\n`;
    }
    
    return result;
  };
  
  // Convert the root element's children
  const rootElement = doc.body.querySelector('root');
  if (!rootElement) return '';
  
  let uxmlContent = '<?xml version="1.0" encoding="utf-8"?>\n';
  uxmlContent += '<ui:UXML xmlns:ui="UnityEngine.UIElements" xmlns:uie="UnityEditor.UIElements">\n';
  
  Array.from(rootElement.childNodes).forEach(child => {
    const childXml = convertNode(child, '  ');
    if (childXml) uxmlContent += childXml;
  });
  
  uxmlContent += '</ui:UXML>';
  
  return uxmlContent;
}

/**
 * Converts CSS to Unity USS format
 */
export function cssToUss(css: string): string {
  // Parse CSS and convert to USS
  let uss = css;
  
  // Unity USS uses similar syntax but has some differences
  // Most properties work the same, but we need to handle some special cases
  
  // Convert flexbox properties to Unity equivalents
  uss = uss.replace(/display:\s*flex/gi, 'display: flex');
  uss = uss.replace(/justify-content:\s*center/gi, 'justify-content: center');
  uss = uss.replace(/justify-content:\s*flex-start/gi, 'justify-content: flex-start');
  uss = uss.replace(/justify-content:\s*flex-end/gi, 'justify-content: flex-end');
  uss = uss.replace(/justify-content:\s*space-between/gi, 'justify-content: space-between');
  uss = uss.replace(/justify-content:\s*space-around/gi, 'justify-content: space-around');
  
  uss = uss.replace(/align-items:\s*center/gi, 'align-items: center');
  uss = uss.replace(/align-items:\s*flex-start/gi, 'align-items: flex-start');
  uss = uss.replace(/align-items:\s*flex-end/gi, 'align-items: flex-end');
  uss = uss.replace(/align-items:\s*stretch/gi, 'align-items: stretch');
  
  uss = uss.replace(/flex-direction:\s*row/gi, 'flex-direction: row');
  uss = uss.replace(/flex-direction:\s*column/gi, 'flex-direction: column');
  
  // Convert linear gradients to Unity format (simplified - Unity has limited gradient support)
  // Unity doesn't support CSS-style gradients in USS, so we'll approximate with a comment
  uss = uss.replace(/background:\s*linear-gradient\([^)]+\)/gi, (match) => {
    // Extract first color from gradient as fallback
    const colorMatch = match.match(/#[0-9a-fA-F]{3,6}|rgba?\([^)]+\)/);
    const fallbackColor = colorMatch ? colorMatch[0] : '#ffffff';
    return `background-color: ${fallbackColor}; /* Original gradient: ${match} - Unity requires separate gradient implementation */`;
  });
  
  // Convert cursor property (Unity doesn't support it, comment it out)
  uss = uss.replace(/cursor:\s*[^;]+;/gi, (match) => {
    return `/* ${match} - Not supported in Unity USS */`;
  });
  
  // Convert transition property (Unity uses different animation system)
  uss = uss.replace(/transition:\s*[^;]+;/gi, (match) => {
    return `/* ${match} - Use Unity's transition properties instead */`;
  });
  
  // Convert box-shadow (Unity has limited shadow support)
  uss = uss.replace(/box-shadow:\s*[^;]+;/gi, (match) => {
    return `/* ${match} - Use Unity's shadow properties if needed */`;
  });
  
  // Handle background-image for image keys
  uss = uss.replace(/background-image:\s*url\(['"]?\{\{img:([a-z0-9_-]+)\}\}['"]?\)/gi, 
    (match, imageKey) => {
      return `background-image: url("Images/${imageKey}.png"); /* Update extension as needed */`;
    });
  
  // Convert font-family (Unity uses limited font support)
  uss = uss.replace(/font-family:\s*[^;]+;/gi, (match) => {
    return `/* ${match} - Set font in Unity Inspector or use -unity-font property */`;
  });
  
  // Add Unity-specific comment at the top
  const header = `/* Unity Style Sheet (USS) 
 * Converted from CSS - Some properties may need manual adjustment
 * Note: Unity UI Toolkit has different capabilities than web CSS
 */\n\n`;
  
  return header + uss;
}

/**
 * Extracts all LocaKeys from HTML content
 */
export function extractLocaKeys(html: string): string[] {
  const locaKeyPattern = /\{\{?([a-zA-Z0-9_.-]+)\}\}?/g;
  const keys = new Set<string>();
  
  let match;
  while ((match = locaKeyPattern.exec(html)) !== null) {
    keys.add(match[1]);
  }
  
  return Array.from(keys);
}

/**
 * Extracts all image keys from HTML content
 */
export function extractImageKeys(html: string): string[] {
  const imageKeyPattern = /\{\{img:([a-z0-9_-]+)\}\}/g;
  const keys = new Set<string>();
  
  let match;
  while ((match = imageKeyPattern.exec(html)) !== null) {
    keys.add(match[1]);
  }
  
  return Array.from(keys);
}

/**
 * Downloads an image from URL and returns as Blob
 */
async function downloadImage(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download image: ${url}`);
  }
  return response.blob();
}

/**
 * Gets file extension from URL or blob type
 */
function getImageExtension(url: string, blob: Blob): string {
  // Try to get extension from blob type
  const mimeType = blob.type;
  if (mimeType === 'image/png') return 'png';
  if (mimeType === 'image/jpeg' || mimeType === 'image/jpg') return 'jpg';
  if (mimeType === 'image/gif') return 'gif';
  if (mimeType === 'image/webp') return 'webp';
  if (mimeType === 'image/svg+xml') return 'svg';
  
  // Fallback to URL extension
  const urlMatch = url.match(/\.([a-z0-9]+)(\?|$)/i);
  if (urlMatch) return urlMatch[1].toLowerCase();
  
  return 'png'; // Default fallback
}

/**
 * Generates README content for Unity integration
 */
function generateReadme(locaKeys: string[], imageKeys: string[]): string {
  return `# Unity UI Toolkit Export - Integration Guide

## Overview
This package contains your UI design exported from LocaNest for use in Unity's UI Toolkit system.

## Package Contents
- **UI.uxml** - UI structure with LocaKey placeholders
- **UI.uss** - Style sheet for the UI
- **Images/** - All referenced images
- **README.txt** - This file

## Installation Steps

### 1. Import Files into Unity
1. Copy this entire folder into your Unity project's Assets directory
2. The recommended location is: Assets/UI/YourUIName/

### 2. Set Up Images
The Images folder contains all assets referenced in your UI:
${imageKeys.length > 0 ? imageKeys.map(key => `- ${key}`).join('\n') : '- No images in this export'}

Unity will automatically import these images. You may need to:
- Adjust import settings (texture type, compression, etc.) in the Inspector
- Ensure images are accessible to UI Toolkit (default settings should work)

### 3. Use the UI in Your Scene
1. Open the UI Builder (Window > UI Toolkit > UI Builder)
2. Load the UI.uxml file
3. Preview and customize as needed
4. To use in a scene, add a UI Document component to a GameObject
5. Assign the UI.uxml file to the Source Asset field

### 4. Set Up Localization for LocaKeys

This UI contains the following LocaKeys that need runtime localization:
${locaKeys.length > 0 ? locaKeys.map(key => `- {{${key}}}`).join('\n') : '- No LocaKeys in this export'}

You need to set up Unity's Localization package to replace these placeholders at runtime.

#### Option A: Unity Localization Package (Recommended)
1. Install Unity Localization package (com.unity.localization) via Package Manager
2. Set up your localization tables with the LocaKeys listed above
3. Use the script below to bind LocaKeys to UI elements

#### Option B: Custom Script
Create a script to replace LocaKeys at runtime. Here's an example:

\`\`\`csharp
using UnityEngine;
using UnityEngine.UIElements;
using System.Collections.Generic;

public class UILocalizationManager : MonoBehaviour
{
    [SerializeField] private UIDocument uiDocument;
    
    // Your localization dictionary
    private Dictionary<string, string> localizedStrings = new Dictionary<string, string>()
    {
${locaKeys.map(key => `        { "${key}", "Localized text for ${key}" }, // TODO: Add actual translation`).join('\n')}
    };
    
    void Start()
    {
        if (uiDocument == null)
        {
            uiDocument = GetComponent<UIDocument>();
        }
        
        LocalizeUI();
    }
    
    void LocalizeUI()
    {
        var root = uiDocument.rootVisualElement;
        
        // Find all labels and buttons with LocaKey patterns
        var labels = root.Query<Label>().ToList();
        foreach (var label in labels)
        {
            label.text = ReplaceLocaKeys(label.text);
        }
        
        var buttons = root.Query<Button>().ToList();
        foreach (var button in buttons)
        {
            button.text = ReplaceLocaKeys(button.text);
        }
    }
    
    string ReplaceLocaKeys(string text)
    {
        if (string.IsNullOrEmpty(text)) return text;
        
        // Replace {{key}} or {key} patterns
        foreach (var kvp in localizedStrings)
        {
            text = text.Replace("{{" + kvp.Key + "}}", kvp.Value);
            text = text.Replace("{" + kvp.Key + "}", kvp.Value);
        }
        
        return text;
    }
    
    // Call this method when language changes
    public void UpdateLanguage(string languageCode)
    {
        // Load new translations for the language
        // Then refresh the UI
        LocalizeUI();
    }
}
\`\`\`

## Important Notes

### CSS to USS Limitations
Unity's USS (Unity Style Sheet) is based on CSS but has some limitations:
- **Gradients**: CSS gradients are not directly supported. They've been converted to solid colors with comments.
- **Animations**: CSS transitions don't work. Use Unity's transition properties or USS animations.
- **Shadows**: box-shadow is not supported. Use Unity's shadow properties if needed.
- **Fonts**: Custom fonts need to be imported and referenced via -unity-font property.
- **Cursors**: Not supported in USS.

### Customization in Unity
After import, you can:
- Modify the UXML structure in UI Builder
- Adjust USS styles to fit Unity's capabilities
- Add Unity-specific properties and bindings
- Connect UI events to C# scripts

### Updating from LocaNest
If you make changes in LocaNest and re-export:
1. Back up any Unity-specific customizations you've made
2. Replace the files with the new export
3. Re-apply your Unity customizations if needed

## Support
For issues with the LocaNest export, refer to LocaNest documentation.
For Unity UI Toolkit questions, see Unity's official documentation:
https://docs.unity3d.com/Manual/UIElements.html

## Version Info
Exported from LocaNest UI Editor
Export Date: ${new Date().toISOString()}
`;
}

/**
 * Main export function - creates a zip file with all necessary files
 */
export async function exportForUnity(
  html: string,
  css: string,
  images: ImageData[],
  templateName?: string
): Promise<Blob> {
  const zip = new JSZip();
  
  // Convert HTML to UXML
  const uxml = htmlToUxml(html);
  zip.file('UI.uxml', uxml);
  
  // Convert CSS to USS
  const uss = cssToUss(css);
  zip.file('UI.uss', uss);
  
  // Extract LocaKeys and image keys
  const locaKeys = extractLocaKeys(html);
  const imageKeys = extractImageKeys(html);
  
  // Download and package images
  const imagesFolder = zip.folder('Images');
  if (imagesFolder && imageKeys.length > 0) {
    for (const imageKey of imageKeys) {
      const imageData = images.find(img => img.imagekey === imageKey);
      if (imageData) {
        try {
          const imageBlob = await downloadImage(imageData.url);
          const extension = getImageExtension(imageData.url, imageBlob);
          imagesFolder.file(`${imageKey}.${extension}`, imageBlob);
        } catch (error) {
          console.error(`Failed to download image ${imageKey}:`, error);
          // Continue with other images
        }
      }
    }
  }
  
  // Generate and add README
  const readme = generateReadme(locaKeys, imageKeys);
  zip.file('README.txt', readme);
  
  // Generate zip file
  const zipBlob = await zip.generateAsync({ 
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  });
  
  return zipBlob;
}

/**
 * Triggers download of the zip file
 */
export function downloadZip(blob: Blob, filename: string = 'unity-ui-export.zip') {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

